import axios from "@/libs/axios";
import { UploadDto } from "@saysimple-platform/dtos";
import { defineStore } from "pinia";

interface State {
    isUploadPending: boolean;
    attachments: UploadDto[];
}

/**
 * To be honest, I don't know exactly if we should Pinia or a composable, so I've asked ChatGPT and agreed to use Pinia:
 *
 * The choice between using a Pinia store or a composable to store file uploads depends on your specific requirements and the complexity of your application. Here's a plan to help you make a decision:
 * Evaluate the complexity: Consider the complexity of managing file uploads in your application. If it's a simple use case with just a few files, using a composable might be sufficient. However, if your application deals with complex file handling scenarios like multiple file types, large file sizes, or extensive file manipulation, a Pinia store might provide better organization and management.
 * Consider reusability: Think about whether you need to reuse the file upload logic across multiple components or modules in your application. If so, a Pinia store might be more suitable as it allows you to centralize and share the state and logic among different components.
 * Evaluate scalability: If your application is expected to handle a large number of concurrent file uploads or needs to manage upload progress, a Pinia store could offer better scalability. With a store, you can track the upload progress, handle errors, and implement features like pausing/resuming uploads more easily.
 * Testability: Both Pinia stores and composables can be unit tested. However, using a Pinia store might provide a more straightforward way to mock and test the file upload functionality separately from the components that consume it.
 */
export const useUploadAttachmentsStore = defineStore("upload-attachments", {
    state: (): State => ({
        isUploadPending: false,
        attachments: [],
    }),
    actions: {
        addAttachment(attachmentDto: UploadDto) {
            this.attachments.push(attachmentDto);
        },
        clearAttachments() {
            this.attachments = [];
        },
        removeAttachment(attachmentDto: UploadDto) {
            const attachmentIndex = this.attachments.findIndex((attachment) => attachment === attachmentDto);
            this.attachments.splice(attachmentIndex, 1);
        },
        fetchAsset(assetId: number) {
            axios.get(`assets/${assetId}`).then((response) => this.addAttachment(response.data));
        },
    },
    getters: {
        hasAttachments: (state) => state.attachments.length > 0,
    },
});
